import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { consentText } from '@mobi/account/Areas/SignUp/Components/Message'
import { DocumentPermission } from '@mobi/account/Areas/SignUp/Components/Verification'
import {
  ALPHANUMERIC_REGEX,
  getNameValidationMessage,
  getRequiredValidationMessage,
  isValidName,
  routes,
} from '@mobi/account/Areas/SignUp/Utils'
import { NameInput } from '@mobi/account/Common'
import { signupTracker } from '@mobi/account/Common/Analytics'
import {
  AlphabeticalCountryList,
  PopularCountryList,
} from '@mobi/account/Common/CountryOptionGroups/CountryOptionGroups'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { StepGroup } from '@mobi/component-library/Base/StepGroup'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { DropdownInput } from '@mobi/component-library/Selection&Input/DropdownInput'
import { Input } from '@mobi/component-library/Selection&Input/Inputs'
import { useApplication, useApplicationSubmit, useCountryList } from '../../Hooks'
import {
  getSignUpState,
  setIsApplicationPendingReview,
  setNumberOfAttemptsToVerify,
  setSignupApplication,
  setSignupVerification,
} from '../../Store'
import { SignupError } from '../SignupError'
import { DocumentBodyStyled, FormLayout } from './Layout.styled'

export const Passport = () => {
  const [passportNumber, setPassportNumber] = React.useState('')
  const [passportCountry, setPassportCountry] = React.useState<string | null>('AUS')
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})
  const [showErrorPopup, setShowErrorPopup] = React.useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const signUpState = useSelector(getSignUpState)
  const application = signUpState.signupApplication
  const verification = signUpState.signupVerification
  const countryList = useCountryList()
  const { applicationSave, applicationSaveStatus } = useApplication({
    setValidationMessages,
    setShowErrorPopup,
  })
  const { applicationSubmit, applicationSubmitStatus } = useApplicationSubmit(
    application.applicationReferenceNumber
  )

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    !!passportNumber &&
    !!passportCountry &&
    isValidName(passportCountry) &&
    isValidName(application.firstName) &&
    isValidName(application.middleName, true) &&
    isValidName(application.surname)
  const isBusy =
    countryList.isLoading ||
    applicationSaveStatus === 'pending' ||
    applicationSubmitStatus === 'pending'

  const onSubmit = () => {
    if (!allowToProceed) {
      return
    }

    dispatch(setNumberOfAttemptsToVerify())

    applicationSave(
      { passportDetails: { number: passportNumber, passportCountry } },
      {
        onSuccess: res => {
          if (res.duplicateExists) {
            return
          }

          applicationSubmit(
            { termsAndConditionsText: consentText, termsAndConditionsConsent: givePermission },
            {
              onSuccess: res => {
                dispatch(setSignupApplication({ ...application, enquiryId: res.enquiryId }))
                dispatch(setIsApplicationPendingReview(res.pendingReview))
                dispatch(
                  setSignupVerification({
                    ...verification,
                    isVerified: res.isVerified,
                    unVerifiedReason: res.unverifiedReason ?? '',
                  })
                )
                if (res.isVerified && !res.pendingReview) {
                  signupTracker({
                    interaction_type: 'ID Verification Success',
                    signUpFlow: signUpState.isVerificationAccount ? 'ID Verification' : 'Standard',
                    attemptNumber: signUpState.numberOfAttemptsToVerify.toString(),
                    optionSelected: 'Passport',
                  })
                  navigate(routes.password)
                  return
                }
                navigate(routes.signUpUnsuccessful)
              },
              onError: () => {
                setShowErrorPopup(true)
              },
            }
          )
        },
      }
    )
  }

  if (!(application.applicationReferenceNumber > 0)) {
    return null
  }

  const resetFieldValidation = (field: keyof typeof validationMessages) =>
    setValidationMessages(prev => ({
      ...prev,
      [field]: '',
    }))

  return (
    <>
      <FormLayout>
        <StepGroup currentStep={3} totalSteps={5} />
        <AccountHeader
          alignItems='start'
          title='Enter your Passport details'
          description={
            <InfoBox color='blue' size='sm' hideIcon={true}>
              Enter your details as they appear on your ID document.
            </InfoBox>
          }
          style={{ marginBottom: 0 }}
        />
        <DocumentBodyStyled>
          <NameInput
            firstName={application.firstName}
            middleName={application.middleName}
            lastName={application.surname}
            setFirstName={firstName =>
              dispatch(setSignupApplication({ ...application, firstName }))
            }
            setMiddleName={middleName =>
              dispatch(setSignupApplication({ ...application, middleName }))
            }
            setLastName={surname => dispatch(setSignupApplication({ ...application, surname }))}
            isDisabled={isBusy}
          />
          <Input
            name='passportNumber'
            label='Passport number'
            maxLength={15}
            placeholder='Enter passport number'
            isDisabled={isBusy}
            value={passportNumber}
            onChange={e => {
              const filteredText = e.target.value?.replace(ALPHANUMERIC_REGEX, '')
              setPassportNumber(filteredText)
            }}
            onFocus={() => resetFieldValidation('passportNumber')}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportNumber: getRequiredValidationMessage(passportNumber),
              }))
            }}
            isInvalid={!!validationMessages.passportNumber}
            helpText={validationMessages.passportNumber}
          />
          <DropdownInput
            name='passportCountry'
            label='Issuing country'
            placeholder='Select country'
            value={passportCountry || ''}
            isDisabled={isBusy}
            onChange={event => {
              setPassportCountry(event.target.value || '')
            }}
            onBlur={() => {
              setValidationMessages(prev => ({
                ...prev,
                passportCountry: getNameValidationMessage(passportCountry),
              }))
            }}
            isInvalid={!!validationMessages.passportCountry}
            helpText={validationMessages.passportCountry}
          >
            <PopularCountryList countries={countryList.data ?? []} />
            <AlphabeticalCountryList countries={countryList.data ?? []} />
          </DropdownInput>
          <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
        </DocumentBodyStyled>

        <footer>
          <Button isFullWidth disabled={!allowToProceed} isLoading={isBusy} onClick={onSubmit}>
            Continue
          </Button>
        </footer>
      </FormLayout>
      <SignupError isOpen={showErrorPopup} onClose={() => setShowErrorPopup(false)} />
    </>
  )
}
